import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import {
  BookingDetailRespDTO,
  BookingDetailSearchReqDTO,
  BookingDetailSearchRespDTO,
  EnquiryDetailRespDTO,
  EnquiryDetailSearchReqDTO,
  EnquiryDetailSearchRespDTO,
  RemarkReqDTO
} from 'src/models/enquiry';
import { tourismClassifiedAPIRoutes } from '..';

export const enquiryDetail = createApi({
  reducerPath: 'enquiryDetail',
  tagTypes: ['EnquiryDetailTag'],
  baseQuery: fetchBaseQuery({
    baseUrl: tourismClassifiedAPIRoutes.baseUrl,
    prepareHeaders: (headers) => {
      // If we have a token set in local storage, let's assume that we should be passing it.
      const token = localStorage.getItem('jwtToken');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  endpoints: (builder) => ({
    findEnquiryDetailById: builder.mutation<EnquiryDetailRespDTO, number>({
      query: (id) => ({
        url: `${tourismClassifiedAPIRoutes.enquiryDetail.endpoint}/${tourismClassifiedAPIRoutes.enquiryDetail.findEnquiryDetailById}/${id}`,
        method: 'GET'
      }),
      invalidatesTags: ['EnquiryDetailTag']
    }),
    getEnquiryDetailBySearchCriteria: builder.query<
      EnquiryDetailSearchRespDTO,
      EnquiryDetailSearchReqDTO
    >({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.enquiryDetail.endpoint}/${tourismClassifiedAPIRoutes.enquiryDetail.getEnquiryDetailBySearchCriteria}`,
        method: 'POST',
        body
      }),
      providesTags: ['EnquiryDetailTag']
    }),
    getEnquiryDetailBySearchCriteriaMut: builder.mutation<
      EnquiryDetailSearchRespDTO,
      EnquiryDetailSearchReqDTO
    >({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.enquiryDetail.endpoint}/${tourismClassifiedAPIRoutes.enquiryDetail.getEnquiryDetailBySearchCriteria}`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['EnquiryDetailTag']
    }),
    saveEnquiryDetail: builder.mutation({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.enquiryDetail.endpoint}/${tourismClassifiedAPIRoutes.enquiryDetail.save}`,
        method: 'POST',
        body: body
      }),
      invalidatesTags: ['EnquiryDetailTag']
    }),
    updateEnquiryDetail: builder.mutation({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.enquiryDetail.endpoint}/${tourismClassifiedAPIRoutes.enquiryDetail.updateEnquiryDetail}`,
        method: 'PUT',
        body: body
      }),
      invalidatesTags: ['EnquiryDetailTag']
    }),
    saveRemark: builder.mutation<RemarkReqDTO, RemarkReqDTO>({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.enquiryDetail.endpoint}/${tourismClassifiedAPIRoutes.enquiryDetail.saveRemark}`,
        method: 'POST',
        body: body
      }),
      invalidatesTags: ['EnquiryDetailTag']
    }),

    getAllRemarkBySearchCriteria: builder.mutation({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.enquiryDetail.endpoint}/${tourismClassifiedAPIRoutes.enquiryDetail.getAllRemarkBySearchCriteria}`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['EnquiryDetailTag']
    }),

    getBookingDetailBySearchCriteria: builder.mutation<
      BookingDetailSearchRespDTO,
      BookingDetailSearchReqDTO
    >({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.otherVendorEnquiryDetail.endpoint}/${tourismClassifiedAPIRoutes.otherVendorEnquiryDetail.getBookingDetailBySearchCriteria}`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['EnquiryDetailTag']
    }),

    findBookingDetailById: builder.mutation<BookingDetailRespDTO, number>({
      query: (id) => ({
        url: `${tourismClassifiedAPIRoutes.otherVendorEnquiryDetail.endpoint}/${tourismClassifiedAPIRoutes.otherVendorEnquiryDetail.findBookingDetailById}/${id}`,
        method: 'GET'
      }),
      invalidatesTags: ['EnquiryDetailTag']
    }),

    updateBookingDetail: builder.mutation({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.otherVendorEnquiryDetail.endpoint}/${tourismClassifiedAPIRoutes.otherVendorEnquiryDetail.updateBookingDetail}`,
        method: 'PUT',
        body: body
      }),
      invalidatesTags: ['EnquiryDetailTag']
    }),

    getBookingDocuments: builder.query<any[], number>({
      query: (bookingId) => ({
        url: `${tourismClassifiedAPIRoutes.enquiryDetail.endpoint}/getBookingDocuments/${bookingId}`,
        method: 'GET'
      }),
      providesTags: ['EnquiryDetailTag']
    }),

    uploadBookingDocument: builder.mutation<
      boolean,
      { formData: FormData; documentType: string; bookingId: number }
    >({
      query: ({ formData, documentType, bookingId }) => ({
        url: `${tourismClassifiedAPIRoutes.fileUpload.endpoint}/${tourismClassifiedAPIRoutes.fileUpload.uploadBookingDocument}/${documentType}/${bookingId}`,
        method: 'POST',
        body: formData
      }),
      invalidatesTags: ['EnquiryDetailTag']
    }),

    deleteBookingDocument: builder.mutation<boolean, number>({
      query: (bookingDocumentId) => ({
        url: `${tourismClassifiedAPIRoutes.fileUpload.endpoint}/${tourismClassifiedAPIRoutes.fileUpload.deleteBookingDocument}/${bookingDocumentId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['EnquiryDetailTag']
    }),

    getWhatsappStatus: builder.query<{ status: string; qr?: string; phone?: string }, string>({
      query: (sessionId) => ({
        url: `${tourismClassifiedAPIRoutes.enquiryDetail.endpoint}/whatsapp/status?sessionId=${sessionId}`,
        method: 'GET'
      })
    }),

    getWhatsappQR: builder.query<{ status: string; qr?: string }, string>({
      query: (sessionId) => ({
        url: `${tourismClassifiedAPIRoutes.enquiryDetail.endpoint}/whatsapp/qr?sessionId=${sessionId}`,
        method: 'GET'
      })
    }),

    logoutWhatsapp: builder.mutation<any, string>({
      query: (sessionId) => ({
        url: `${tourismClassifiedAPIRoutes.enquiryDetail.endpoint}/whatsapp/logout?sessionId=${sessionId}`,
        method: 'POST'
      })
    }),

    sendWhatsappMessage: builder.mutation<any, { bookingId: number; message: string; sessionId?: string }>({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.enquiryDetail.endpoint}/whatsapp/sendMessage`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['EnquiryDetailTag']
    }),
    exportEnquiryDetailExcel: builder.mutation<Blob, EnquiryDetailSearchReqDTO>({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.enquiryDetail.endpoint}/export/excel`,
        method: 'POST',
        body,
        responseHandler: (response) => response.blob()
      })
    }),
    exportEnquiryDetailPdf: builder.mutation<Blob, EnquiryDetailSearchReqDTO>({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.enquiryDetail.endpoint}/export/pdf`,
        method: 'POST',
        body,
        responseHandler: (response) => response.blob()
      })
    })
  })
});
export const {
  useFindEnquiryDetailByIdMutation,
  useGetEnquiryDetailBySearchCriteriaQuery,
  useSaveEnquiryDetailMutation,
  useUpdateEnquiryDetailMutation,
  useGetEnquiryDetailBySearchCriteriaMutMutation,
  useGetAllRemarkBySearchCriteriaMutation,
  useSaveRemarkMutation,
  useFindBookingDetailByIdMutation,
  useGetBookingDetailBySearchCriteriaMutation,
  useUpdateBookingDetailMutation,
  useGetBookingDocumentsQuery,
  useUploadBookingDocumentMutation,
  useDeleteBookingDocumentMutation,
  useGetWhatsappStatusQuery,
  useLazyGetWhatsappStatusQuery,
  useLazyGetWhatsappQRQuery,
  useLogoutWhatsappMutation,
  useSendWhatsappMessageMutation,
  useExportEnquiryDetailExcelMutation,
  useExportEnquiryDetailPdfMutation
} = enquiryDetail;
